import React, { useState, useEffect } from 'react';
import { 
  X, 
  Send, 
  Heart, 
  MessageSquare, 
  User, 
  ArrowLeft, 
  MoreHorizontal,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useParams, useNavigate } from 'react-router-dom';
import { Post, Comment, getPostComments, createComment, toggleLike } from '@/db/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { toast } from 'sonner';
import { supabase } from '@/db/supabase';

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPostAndComments = async () => {
    if (!postId) return;
    try {
      const { data: postData, error: postError } = await supabase
        .from('community_posts')
        .select('*, profiles(username, avatar_url)')
        .eq('id', postId)
        .single();
      
      if (postError) throw postError;
      setPost(postData);

      const commentsData = await getPostComments(postId);
      setComments(commentsData);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPostAndComments();
  }, [postId]);

  const handleSubmitComment = async () => {
    if (!profile) {
      toast.error('请先登录');
      return;
    }
    if (!commentText.trim()) {
      toast.error('评论内容不能为空');
      return;
    }

    setIsSubmitting(true);
    try {
      const newComment = await createComment({
        post_id: postId!,
        user_id: profile.id,
        content: commentText.trim(),
        parent_id: replyTo || null
      });
      setComments(prev => [...prev, newComment]);
      setCommentText('');
      setReplyTo(null);
      toast.success('评论成功');
    } catch (err) {
      toast.error('评论失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!profile) {
      toast.error('请先登录');
      return;
    }
    try {
      await toggleLike(profile.id, commentId, 'comment');
      fetchPostAndComments();
    } catch (err) {
      toast.error('操作失败');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );

  if (!post) return (
    <div className="min-h-screen flex items-center justify-center">
      <Card className="p-8 text-center">
        <h2 className="text-xl font-bold mb-4">帖子不存在</h2>
        <Button onClick={() => navigate('/community')}>返回社区</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      <div className="tech-flow-bg h-[160px] flex items-center px-6">
        <div className="container mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 mb-2 gap-2"
            onClick={() => navigate('/community')}
          >
            <ArrowLeft className="h-4 w-4" />
            返回社区
          </Button>
          <h1 className="text-3xl font-bold text-white">帖子详情</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8 max-w-4xl">
        {/* 帖子内容 */}
        <Card className="rounded-xl glass-card border-border/50 shadow-lg mb-6">
          <CardHeader className="p-6 border-b border-border/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-primary/20">
                  <AvatarImage src={post.profiles?.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-base font-bold text-foreground">
                    {post.profiles?.username || '用户'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: zhCN })}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => {}}>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold mb-4">{post.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-base leading-relaxed mb-6 whitespace-pre-wrap">{post.content}</p>
            
            {post.images && post.images.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                {post.images.map((img, i) => (
                  <div key={i} className="aspect-video rounded-lg overflow-hidden bg-muted">
                    <img src={img} alt="post" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags?.map((tag) => (
                <Badge key={tag} className="bg-primary/10 text-primary border-primary/20">
                  #{tag}
                </Badge>
              ))}
            </div>

            <div className="flex items-center gap-6 pt-4 border-t border-border/50">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Heart className="h-5 w-5" />
                <span className="font-bold">{post.likes_count}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MessageSquare className="h-5 w-5" />
                <span className="font-bold">{post.comments_count}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 评论区 */}
        <Card className="rounded-xl glass-card border-border/50 shadow-lg">
          <CardHeader className="p-6 border-b border-border/50">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              全部评论 ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* 发表评论 */}
            <div className="space-y-3">
              {replyTo && (
                <div className="flex items-center justify-between bg-primary/5 p-2 rounded-lg">
                  <span className="text-sm text-muted-foreground">回复评论中...</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setReplyTo(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <Textarea 
                placeholder="发表你的看法..." 
                className="rounded-lg border-primary/20 bg-background/50 min-h-[100px] resize-none"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <div className="flex justify-end">
                <Button 
                  className="bg-primary text-white hover:bg-primary/90 gap-2"
                  onClick={handleSubmitComment}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  发表评论
                </Button>
              </div>
            </div>

            {/* 评论列表 */}
            <div className="space-y-4">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <Avatar className="h-10 w-10 border border-primary/10">
                      <AvatarImage src={comment.profiles?.avatar_url} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-bold text-sm">{comment.profiles?.username || '用户'}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: zhCN })}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm leading-relaxed mb-3">{comment.content}</p>
                      <div className="flex items-center gap-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 gap-1 text-muted-foreground hover:text-primary"
                          onClick={() => handleLikeComment(comment.id)}
                        >
                          <Heart className="h-3.5 w-3.5" />
                          <span className="text-xs">{comment.likes_count}</span>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 gap-1 text-muted-foreground hover:text-primary"
                          onClick={() => setReplyTo(comment.id)}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          <span className="text-xs">回复</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-muted-foreground">
                  暂无评论，快来发表第一条评论吧！
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PostDetailPage;
